<template>
  <div v-if="pageLoading" class="m-3">
    <placeholder-content :is-load="true" />
  </div>
  <div v-else-if="!hasPermission" class="m-3">
    <access-denied />
  </div>
  <div v-else-if="featureUnavailable" class="m-3">
    <feature-not-available back-route="reports" back-text="_plan.Back to reports" />
  </div>
  <div v-else class="p-3">
    <!-- Header -->
    <div class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3 gap-2">
      <div class="d-flex align-items-center gap-2">
        <div>
          <h5 class="fw-bold mb-0">
            <i class="fa-solid fa-receipt me-2" style="color: #ef4444;"></i>{{ $t('Expense Report') }}
          </h5>
          <small class="text-muted">{{ $t('Operating expenses for handover to accountant') }}</small>
        </div>
      </div>
      <div class="d-flex gap-2">
        <button class="btn btn-outline-success btn-sm" @click="exportExcel" :disabled="loading || !rows.length">
          <i class="fa-solid fa-file-excel me-1"></i>{{ $t('Export Excel') }}
        </button>
        <button class="btn btn-outline-primary btn-sm" @click="downloadPdf" :disabled="pdfLoading || loading">
          <i class="fa-solid fa-download me-1"></i>{{ $t('Download') }}
        </button>
        <button class="btn btn-primary btn-sm" @click="previewPdf" :disabled="pdfLoading || loading">
          <span v-if="pdfLoading"><i class="fa-solid fa-spinner fa-spin me-1"></i>{{ $t('Generating') }}...</span>
          <span v-else><i class="fa-solid fa-file-pdf me-1"></i>{{ $t('Preview PDF') }}</span>
        </button>
      </div>
    </div>

    <!-- Filters -->
    <div class="card border-0 shadow-sm mb-4">
      <div class="card-body py-2 px-3">
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <select v-model="filters.branch" class="form-select form-select-sm" style="min-width: 140px; max-width: 200px;" :disabled="loading">
            <option value="all">{{ $t('All Branches') }}</option>
            <option v-for="b in branches" :key="b.id" :value="b.id">{{ b.name }}</option>
          </select>
          <select v-model="categoryFilter" class="form-select form-select-sm" style="min-width: 140px; max-width: 200px;" :disabled="loading">
            <option value="">{{ $t('All Categories') }}</option>
            <option v-for="c in categories" :key="c.id" :value="c.id">{{ $t(c.name) }}</option>
          </select>
          <div class="input-group input-group-sm w-auto date-range-input-group">
            <span class="input-group-text"><i class="fa-regular fa-calendar"></i></span>
            <date-range-picker
              v-model="filters.date_range"
              :date-range="filters.date_range"
              :locale-data="dateRangeLocale"
              :ranges="dateRanges"
              :opens="'right'"
              :auto-apply="true"
              :show-dropdowns="true"
              :control-container-class="''"
            >
              <template v-slot:input="picker">
                <span v-if="picker.startDate && picker.endDate">
                  {{ formatDate(picker.startDate) }} — {{ formatDate(picker.endDate) }}
                </span>
                <span v-else class="text-muted">{{ $t('Select date range') }}</span>
              </template>
            </date-range-picker>
          </div>
        </div>
      </div>
    </div>

    <!-- Error -->
    <div v-if="errorMessage" class="alert alert-danger d-flex justify-content-between align-items-center" role="alert">
      <span><i class="fa-solid fa-triangle-exclamation me-2"></i>{{ errorMessage }}</span>
      <button class="btn btn-sm btn-outline-danger" @click="loadReport">{{ $t('Retry') }}</button>
    </div>

    <div :class="{ 'report-loading': loading }">

    <!-- KPI Cards -->
    <div class="row g-3 mb-4">
      <div class="col-6 col-md-4">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-body p-3 text-center">
            <div class="text-muted small mb-1">{{ $t('Total Expense') }}</div>
            <div class="fw-bold fs-4 text-danger">{{ formatCurrency(summary.total_expense) }}</div>
          </div>
        </div>
      </div>
      <div class="col-6 col-md-4">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-body p-3 text-center">
            <div class="text-muted small mb-1">{{ $t('Transactions') }}</div>
            <div class="fw-bold fs-4">{{ summary.total_count || 0 }}</div>
          </div>
        </div>
      </div>
      <div class="col-12 col-md-4">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-body p-3 text-center">
            <div class="text-muted small mb-1">{{ $t('Top Category') }}</div>
            <div class="fw-bold fs-5">{{ topCategory ? $t(topCategory.category) : '-' }}</div>
            <div class="small text-muted">{{ topCategory ? formatCurrency(topCategory.amount) : '' }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Breakdown by Category -->
    <div v-if="summary.breakdown && summary.breakdown.length" class="card border-0 shadow-sm mb-4">
      <div class="card-header bg-white border-0 py-3">
        <h6 class="mb-0 fw-semibold"><i class="fa-solid fa-chart-pie me-2 text-danger"></i>{{ $t('Expense by Category') }}</h6>
      </div>
      <div class="card-body p-0">
        <div class="table-responsive">
          <table class="table table-hover table-sm mb-0 align-middle">
            <thead class="table-light">
              <tr>
                <th class="ps-3">{{ $t('Category') }}</th>
                <th class="text-end">{{ $t('Transactions') }}</th>
                <th class="text-end pe-3">{{ $t('Amount') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in summary.breakdown" :key="item.category">
                <td class="ps-3">{{ $t(item.category) }}</td>
                <td class="text-end">{{ item.count }}</td>
                <td class="text-end pe-3">{{ formatCurrency(item.amount) }}</td>
              </tr>
            </tbody>
            <tfoot class="table-light fw-bold">
              <tr>
                <td class="ps-3">{{ $t('Total') }}</td>
                <td class="text-end">{{ summary.total_count || 0 }}</td>
                <td class="text-end pe-3 text-danger">{{ formatCurrency(summary.total_expense) }}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>

    <!-- Detail Table -->
    <div class="card border-0 shadow-sm mb-4">
      <div class="card-header bg-white border-0 py-3">
        <h6 class="mb-0 fw-semibold"><i class="fa-solid fa-table me-2 text-secondary"></i>{{ $t('Expense Details') }}</h6>
      </div>
      <div class="card-body p-0">
        <div class="table-responsive">
          <table class="table table-hover table-sm mb-0 align-middle">
            <thead class="table-light">
              <tr>
                <th class="ps-3 text-end" style="width: 50px;">#</th>
                <th>{{ $t('Expense Date') }}</th>
                <th>{{ $t('Reference No.') }}</th>
                <th>{{ $t('Category') }}</th>
                <th>{{ $t('Payee') }}</th>
                <th>{{ $t('Description') }}</th>
                <th>{{ $t('Branch') }}</th>
                <th class="text-end pe-3">{{ $t('Amount') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!rows.length && !loading">
                <td colspan="8" class="text-center text-muted py-4">{{ $t('No data') }}</td>
              </tr>
              <tr v-for="row in rows" :key="row.seq">
                <td class="ps-3 text-end">{{ row.seq }}</td>
                <td class="text-nowrap">{{ row.date }}</td>
                <td>{{ row.reference_no || '-' }}</td>
                <td>{{ $t(row.category) }}</td>
                <td>{{ row.payee || '-' }}</td>
                <td>{{ row.message }}</td>
                <td>{{ row.branch ? $t(row.branch) : $t('No branch') }}</td>
                <td class="text-end pe-3">{{ formatCurrency(row.amount) }}</td>
              </tr>
            </tbody>
            <tfoot v-if="rows.length" class="table-light fw-bold">
              <tr>
                <td class="ps-3" colspan="7">{{ $t('Total') }}</td>
                <td class="text-end pe-3 text-danger">{{ formatCurrency(summary.total_expense) }}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>

    </div><!-- /loading wrapper -->

    <div v-if="loading" class="report-loading-overlay">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">{{ $t('Loading') + '...' }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import { Reports } from '@/services/endpoint/reports.service';
import { exportRowsToXlsx, MONEY_FMT } from '@/utils/excelExport';
import reportMixin from './reportMixin';

export default {
  name: 'ReportExpense',
  mixins: [reportMixin],
  data() {
    const start = moment().startOf('month').toDate();
    const end = moment().endOf('month').toDate();
    return {
      reportLabel: 'Expense Report',
      filters: {
        branch: 'all',
        date_range: { startDate: start, endDate: end },
        dateFrom: moment(start).format('YYYY-MM-DD'),
        dateTo: moment(end).format('YYYY-MM-DD'),
      },
      categoryFilter: '',
      categories: [],
      summary: {
        total_expense: 0,
        total_count: 0,
        breakdown: [],
      },
      rows: [],
    };
  },
  computed: {
    topCategory() {
      return (this.summary.breakdown && this.summary.breakdown[0]) || null;
    },
  },
  watch: {
    categoryFilter() {
      this.loadReport();
    },
  },
  methods: {
    buildFetchParams() {
      return {
        branch_id: this.filters.branch,
        start: this.formatDate(this.filters.date_range.startDate),
        end: this.formatDate(this.filters.date_range.endDate),
        expense_category_id: this.categoryFilter || undefined,
      };
    },

    async fetchReport() {
      const res = await Reports.expenseReport(this.buildFetchParams());
      const payload = res?.data?.data || {};
      const filter = res?.data?.filter || {};
      this.branches = filter.branches || [];
      this.categories = filter.categories || [];
      this.summary = payload.summary || this.summary;
      this.rows = payload.rows || [];
    },

    toNum(v) {
      const n = parseFloat(v);
      return Number.isFinite(n) ? n : 0;
    },

    exportExcel() {
      const from = this.formatDate(this.filters.date_range.startDate);
      const to = this.formatDate(this.filters.date_range.endDate);
      const columns = [
        { header: '#', key: 'seq', width: 6 },
        { header: this.$t('Expense Date'), key: 'date', width: 14 },
        { header: this.$t('Reference No.'), key: 'reference_no', width: 16 },
        { header: this.$t('Category'), value: r => this.$t(r.category || ''), width: 18 },
        { header: this.$t('Payee'), key: 'payee', width: 20 },
        { header: this.$t('Description'), key: 'message', width: 34 },
        { header: this.$t('Branch'), value: r => (r.branch ? this.$t(r.branch) : this.$t('No branch')), width: 18 },
        { header: this.$t('Amount'), value: r => this.toNum(r.amount), width: 16, numFmt: MONEY_FMT },
      ];
      const preRows = [
        [this.$t('Expense Report')],
        [this.selectedBranchLabel + '  |  ' + from + ' — ' + to],
        [this.$t('Transactions') + ': ' + (this.summary.total_count || 0), this.$t('Total Expense') + ': ' + this.toNum(this.summary.total_expense)],
        [],
      ];
      const postRows = [
        [this.$t('Total'), '', '', '', '', '', '', this.toNum(this.summary.total_expense)],
      ];
      exportRowsToXlsx({
        filename: `expenses-${from}-${to}.xlsx`,
        sheetName: 'Expenses',
        columns,
        rows: this.rows,
        preRows,
        postRows,
      });
    },

    async buildPdfDefinition() {
      const content = [
        ...this.buildPdfHeader(),
        {
          columns: [
            { text: [{ text: this.$t('Total Expense') + '\n', style: 'kpiLabel' }, { text: this.formatCurrencyRaw(this.summary.total_expense), style: 'kpiValue' }], width: '*' },
            { text: [{ text: this.$t('Transactions') + '\n', style: 'kpiLabel' }, { text: String(this.summary.total_count || 0), style: 'kpiValue' }], width: '*' },
          ],
          margin: [0, 0, 0, 16],
        },
        { text: this.$t('Expense by Category'), style: 'sectionTitle' },
        {
          table: {
            headerRows: 1,
            widths: ['*', 'auto', 'auto'],
            body: [
              [
                { text: this.$t('Category'), style: 'tableHeader' },
                { text: this.$t('Transactions'), style: 'tableHeader', alignment: 'right' },
                { text: this.$t('Amount'), style: 'tableHeader', alignment: 'right' },
              ],
              ...(this.summary.breakdown || []).map(item => [
                { text: this.$t(item.category), style: 'tableCell' },
                { text: String(item.count), style: 'tableCell', alignment: 'right' },
                { text: this.formatCurrencyRaw(item.amount), style: 'tableCell', alignment: 'right' },
              ]),
              [
                { text: this.$t('Total'), style: 'tableHeader' },
                { text: String(this.summary.total_count || 0), style: 'tableHeader', alignment: 'right' },
                { text: this.formatCurrencyRaw(this.summary.total_expense), style: 'tableHeader', alignment: 'right' },
              ],
            ],
          },
          layout: 'lightHorizontalLines',
          margin: [0, 0, 0, 16],
        },
        { text: this.$t('Expense Details'), style: 'sectionTitle' },
        {
          table: {
            headerRows: 1,
            /**
             * กว้างตายตัวทุกคอลัมน์ ห้ามใช้ 'auto'/'*' — ตารางนี้มีช่องข้อความอิสระ 4 ช่อง
             * (เลขที่อ้างอิง/ประเภท/ผู้รับเงิน/รายละเอียด) ที่ผู้ใช้พิมพ์เองยาวแค่ไหนก็ได้
             * ภาษาไทยไม่มีช่องว่าง pdfmake จึงตัดคำไม่ได้และดันตารางทะลุขอบกระดาษ
             * (วัดจริง: แนวตั้ง 575pt จากที่มี 555pt · แนวนอนแบบ auto ก็ยังล้นที่ 892pt)
             * รวม 648pt + ค่าคงที่ของ pdfmake ≈ 800pt จากพื้นที่ 802pt ของ A4 แนวนอน
             */
            widths: [20, 56, 80, 80, 110, 170, 76, 56],
            body: [
              [
                { text: '#', style: 'tableHeader', alignment: 'right' },
                { text: this.$t('Expense Date'), style: 'tableHeader' },
                { text: this.$t('Reference No.'), style: 'tableHeader' },
                { text: this.$t('Category'), style: 'tableHeader' },
                { text: this.$t('Payee'), style: 'tableHeader' },
                { text: this.$t('Description'), style: 'tableHeader' },
                { text: this.$t('Branch'), style: 'tableHeader' },
                { text: this.$t('Amount'), style: 'tableHeader', alignment: 'right' },
              ],
              ...this.rows.map(r => [
                { text: String(r.seq), style: 'tableCell', alignment: 'right' },
                { text: r.date, style: 'tableCell' },
                { text: r.reference_no || '-', style: 'tableCell' },
                { text: this.$t(r.category), style: 'tableCell' },
                { text: r.payee || '-', style: 'tableCell' },
                { text: r.message, style: 'tableCell' },
                { text: r.branch ? this.$t(r.branch) : this.$t('No branch'), style: 'tableCell' },
                { text: this.formatCurrencyRaw(r.amount), style: 'tableCell', alignment: 'right' },
              ]),
              [
                { text: this.$t('Total'), style: 'tableHeader', colSpan: 7 }, {}, {}, {}, {}, {}, {},
                { text: this.formatCurrencyRaw(this.summary.total_expense), style: 'tableHeader', alignment: 'right' },
              ],
            ],
          },
          layout: 'lightHorizontalLines',
        },
      ];
      return { pageOrientation: 'landscape', content };
    },
  },
};
</script>

<style scoped>
.report-loading {
  opacity: 0.5;
  pointer-events: none;
  transition: opacity 0.2s;
}
.report-loading-overlay {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1050;
}
</style>
